<template>
  <v-app id="inspire" style="background:#F9F9F9;">
    <vue-header-admin></vue-header-admin>
    <v-main class="mb-10">
      <v-container fluid class="px-6 py-6">
        <v-row>
          <v-col cols="12" md="8">
            <h1 class="text-h4 font-weight-bold text-typo mb-2">External API Keys</h1>
            <p class="text-body-2 text--secondary">Manage API keys for third-party inventory integrations</p>
          </v-col>
          <v-col cols="12" md="4" class="text-right">
            <v-btn color="green" dark @click="openAddDialog">
              <v-icon left class="material-icons-round">add</v-icon>
              Generate New API Key
            </v-btn>
          </v-col>
        </v-row>

        <!-- API Keys Table -->
        <v-row>
          <v-col cols="12">
            <v-card class="card-shadow border-radius-xl mb-4">
              <v-card-title class="d-flex justify-space-between">
                <span>API Keys</span>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search..."
                  single-line
                  hide-details
                  dense
                  outlined
                  style="max-width: 300px;"
                ></v-text-field>
              </v-card-title>

              <v-card-text>
                <v-data-table
                  :headers="headers"
                  :items="apiKeys"
                  :search="search"
                  :loading="loading"
                  class="elevation-1"
                  :items-per-page="10"
                >
                  <template v-slot:item.is_active="{ item }">
                    <v-chip :color="item.is_active ? 'success' : 'error'" small>
                      {{ item.is_active ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </template>

                  <template v-slot:item.last_used_at="{ item }">
                    <span v-if="item.last_used_at">{{ formatDate(item.last_used_at) }}</span>
                    <span v-else class="text--secondary">Never</span>
                  </template>

                  <template v-slot:item.expires_at="{ item }">
                    <span v-if="item.expires_at">{{ formatDate(item.expires_at) }}</span>
                    <span v-else class="text--secondary">Never</span>
                  </template>

                  <template v-slot:item.rate_limit="{ item }">
                    <v-chip color="primary" small>{{ item.rate_limit }}/min</v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small @click="editApiKey(item)" v-bind="attrs" v-on="on">
                          <v-icon small class="material-icons-round">edit</v-icon>
                        </v-btn>
                      </template>
                      <span>Edit</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small @click="regenerateKey(item)" v-bind="attrs" v-on="on">
                          <v-icon small class="material-icons-round">refresh</v-icon>
                        </v-btn>
                      </template>
                      <span>Regenerate Credentials</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small color="error" @click="deleteApiKey(item)" v-bind="attrs" v-on="on">
                          <v-icon small class="material-icons-round">delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Revoke Key</span>
                    </v-tooltip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Info Card -->
        <v-row>
          <v-col cols="12">
            <v-alert type="info" outlined>
              <strong>API Documentation:</strong> Share the API documentation with third-party systems. 
              They will need the API Key and API Secret to authenticate requests.
              <br><br>
              <strong>Endpoints:</strong>
              <ul>
                <li><code>GET /api/external/v1/inventory</code> - List inventory with filters</li>
                <li><code>GET /api/external/v1/inventory/stats</code> - Get inventory statistics</li>
                <li><code>GET /api/external/v1/inventory/{id}</code> - Get single vehicle details</li>
              </ul>
            </v-alert>
          </v-col>
        </v-row>
      </v-container>

      <!-- Add/Edit Dialog -->
      <v-dialog v-model="dialog" max-width="600px" persistent>
        <v-card>
          <v-card-title>
            {{ editMode ? 'Edit API Key' : 'Generate New API Key' }}
          </v-card-title>
          <v-card-text>
            <v-form ref="form" v-model="formValid">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedItem.name"
                    label="Name (e.g., Partner System Name)"
                    outlined
                    :rules="[v => !!v || 'Name is required']"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="editedItem.description"
                    label="Description (optional)"
                    outlined
                    rows="2"
                  ></v-textarea>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="editedItem.rate_limit"
                    label="Rate Limit (requests/minute)"
                    type="number"
                    outlined
                    :rules="[v => v > 0 || 'Must be greater than 0']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="editedItem.expires_at"
                        label="Expiration Date (optional)"
                        readonly
                        outlined
                        clearable
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="editedItem.expires_at" @input="dateMenu = false"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="6" v-if="editMode">
                  <v-switch
                    v-model="editedItem.is_active"
                    label="Active"
                    color="green"
                  ></v-switch>
                </v-col>
                <v-col cols="12">
                  <v-combobox
                    v-model="editedItem.ip_whitelist"
                    label="IP Whitelist (leave empty for any IP)"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    outlined
                    hint="Press Enter after each IP address"
                    persistent-hint
                  ></v-combobox>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeDialog">Cancel</v-btn>
            <v-btn color="green" dark @click="saveApiKey" :loading="saving">
              {{ editMode ? 'Update' : 'Generate' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Credentials Dialog (shown after creating new key) -->
      <v-dialog v-model="credentialsDialog" max-width="700px" persistent>
        <v-card>
          <v-card-title class="headline">
            <v-icon left color="success">mdi-check-circle</v-icon>
            API Key Generated Successfully
          </v-card-title>
          <v-card-text>
            <v-alert type="warning" class="mb-4">
              <strong>Important:</strong> Save these credentials now! They will only be shown once.
            </v-alert>

            <v-text-field
              :value="newCredentials.api_key"
              label="API Key"
              readonly
              outlined
              append-icon="mdi-content-copy"
              @click:append="copyToClipboard(newCredentials.api_key, 'API Key')"
            ></v-text-field>

            <v-text-field
              :value="newCredentials.api_secret"
              label="API Secret"
              readonly
              outlined
              append-icon="mdi-content-copy"
              @click:append="copyToClipboard(newCredentials.api_secret, 'API Secret')"
            ></v-text-field>

            <v-expansion-panels class="mt-4">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  Example Usage (cURL)
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <pre class="pa-3 grey lighten-4" style="overflow-x: auto; font-size: 12px;">curl -X GET "{{ apiBaseUrl }}/api/external/v1/inventory" \
  -H "X-API-Key: {{ newCredentials.api_key }}" \
  -H "X-API-Secret: {{ newCredentials.api_secret }}" \
  -H "Accept: application/json"</pre>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green" dark @click="credentialsDialog = false">
              I've Saved the Credentials
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation -->
      <v-dialog v-model="deleteDialog" max-width="400px">
        <v-card>
          <v-card-title>Revoke API Key?</v-card-title>
          <v-card-text>
            Are you sure you want to revoke the API key "<strong>{{ itemToDelete ? itemToDelete.name : '' }}</strong>"?
            This action cannot be undone and any systems using this key will lose access immediately.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteDialog = false">Cancel</v-btn>
            <v-btn color="error" dark @click="confirmDelete" :loading="deleting">Revoke</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-main>
  </v-app>
</template>

<script>
import HeaderAdmin from '@/components/Layout/HeaderAdmin'

export default {
  name: 'ApiKeys',
  components: {
    'vue-header-admin': HeaderAdmin
  },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      search: '',
      dialog: false,
      editMode: false,
      dateMenu: false,
      credentialsDialog: false,
      deleteDialog: false,
      formValid: false,
      apiKeys: [],
      itemToDelete: null,
      newCredentials: {
        api_key: '',
        api_secret: ''
      },
      editedItem: {
        id: null,
        name: '',
        description: '',
        rate_limit: 100,
        expires_at: null,
        ip_whitelist: [],
        is_active: true
      },
      defaultItem: {
        id: null,
        name: '',
        description: '',
        rate_limit: 100,
        expires_at: null,
        ip_whitelist: [],
        is_active: true
      },
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Status', value: 'is_active' },
        { text: 'Rate Limit', value: 'rate_limit' },
        { text: 'Last Used', value: 'last_used_at' },
        { text: 'Expires', value: 'expires_at' },
        { text: 'Created', value: 'created_at' },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    apiBaseUrl() {
      return process.env.VUE_APP_API_URL || window.location.origin
    }
  },
  mounted() {
    this.fetchApiKeys()
  },
  methods: {
    async fetchApiKeys() {
      this.loading = true
      try {
        const response = await axios.get('/api/api-keys')
        if (response.data.flag === 1) {
          this.apiKeys = response.data.data
        }
      } catch (error) {
        this.$toast.error('Error loading API keys')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    openAddDialog() {
      this.editMode = false
      this.editedItem = { ...this.defaultItem }
      this.dialog = true
    },
    editApiKey(item) {
      this.editMode = true
      this.editedItem = {
        ...item,
        ip_whitelist: item.ip_whitelist || []
      }
      this.dialog = true
    },
    closeDialog() {
      this.dialog = false
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
    },
    async saveApiKey() {
      if (!this.$refs.form.validate()) return

      this.saving = true
      try {
        if (this.editMode) {
          const response = await axios.put(`/api/api-keys/${this.editedItem.id}`, this.editedItem)
          if (response.data.flag === 1) {
            this.$toast.success('API key updated successfully')
            this.fetchApiKeys()
            this.closeDialog()
          }
        } else {
          const response = await axios.post('/api/api-keys', this.editedItem)
          if (response.data.flag === 1) {
            this.newCredentials = {
              api_key: response.data.data.api_key,
              api_secret: response.data.data.api_secret
            }
            this.closeDialog()
            this.$nextTick(() => {
              this.credentialsDialog = true
            })
            this.fetchApiKeys()
          }
        }
      } catch (error) {
        this.$toast.error(error.response && error.response.data ? error.response.data.message : 'Error saving API key')
      } finally {
        this.saving = false
      }
    },
    async regenerateKey(item) {
      if (!confirm(`Regenerate credentials for "${item.name}"? Current credentials will stop working immediately.`)) return

      try {
        const response = await axios.post(`/api/api-keys/${item.id}/regenerate`)
        if (response.data.flag === 1) {
          this.newCredentials = {
            api_key: response.data.data.api_key,
            api_secret: response.data.data.api_secret
          }
          this.credentialsDialog = true
        }
      } catch (error) {
        this.$toast.error('Error regenerating credentials')
      }
    },
    deleteApiKey(item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },
    async confirmDelete() {
      this.deleting = true
      try {
        const response = await axios.delete(`/api/api-keys/${this.itemToDelete.id}`)
        if (response.data.flag === 1) {
          this.$toast.success('API key revoked')
          this.fetchApiKeys()
        }
      } catch (error) {
        this.$toast.error('Error revoking API key')
      } finally {
        this.deleting = false
        this.deleteDialog = false
        this.itemToDelete = null
      }
    },
    copyToClipboard(text, label) {
      navigator.clipboard.writeText(text)
      this.$toast.success(`${label} copied to clipboard`)
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString()
    }
  }
}
</script>

<style scoped>
pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>

